import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useApi from '../api';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormatter';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { Input } from "@/components/ui/input";
import { TabsContent } from "@/components/ui/tabs";
import { LoadingState } from '@/components/ui/LoadingState';
import { Eye, Search } from 'lucide-react';
import OrganizationCombobox from './OrganizationCombobox';

interface Organization {
  id: string;
  name: string;
}

interface VisitorLead {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  company?: string | null;
  job_title?: string | null;
}

interface AdminVisitorRow {
  id: string;
  visitor_id: string | null;
  organization_id: string | null;
  organization_name?: string | null;
  lead_id?: string | null;
  lead?: VisitorLead | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  last_activity_at?: string | null;
  conversation_count?: number | null;
  session_count?: number | null;
  person?: Record<string, unknown> | null;
  company?: Record<string, unknown> | null;
}

const VisitorsTab: React.FC = () => {
  const { get } = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [visitors, setVisitors] = useState<AdminVisitorRow[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const selectedOrg = searchParams.get('org') || 'all';
  const setSelectedOrg = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next && next !== 'all') {
      params.set('org', next);
    } else {
      params.delete('org');
    }
    setSearchParams(params, { replace: false });
  };
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [visitorIdQuery, setVisitorIdQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVisitors, setTotalVisitors] = useState(0);
  const perPage = 25;

  const handleFindVisitorById = () => {
    const id = visitorIdQuery.trim();
    if (!id) {
      toast.error('Please enter a visitor ID');
      return;
    }
    navigate(`/admin/visitors/${id}`);
  };

  const fetchOrganizations = async () => {
    try {
      const response = await get('/admin/organizations/');
      setOrganizations(response.data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    }
  };

  const fetchVisitors = async (pageNum: number) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(pageNum),
        per_page: String(perPage),
      });
      if (selectedOrg !== 'all') {
        params.set('organization_id', selectedOrg);
      }
      if (search.trim()) {
        params.set('search', search.trim());
      }
      const response = await get(`/admin/visitors?${params.toString()}`);
      setVisitors(response.data.items || []);
      setTotalPages(response.data.pages || 1);
      setTotalVisitors(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching visitors:', error);
      toast.error('Failed to load visitors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    fetchVisitors(page);
  }, [page, selectedOrg, search]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [selectedOrg, search]);

  const personField = (row: AdminVisitorRow, field: string): string | null => {
    const person = row.person as Record<string, unknown> | null | undefined;
    if (!person) return null;
    const value = person[field];
    return typeof value === 'string' && value ? value : null;
  };

  const getDisplayName = (row: AdminVisitorRow): string => {
    if (row.lead) {
      const parts = [row.lead.first_name, row.lead.last_name].filter(Boolean).join(' ').trim();
      if (parts) return parts;
      if (row.lead.email) return row.lead.email;
    }
    const fullName = personField(row, 'full_name');
    if (fullName) return fullName;
    const first = personField(row, 'first_name');
    const last = personField(row, 'last_name');
    const combined = [first, last].filter(Boolean).join(' ').trim();
    if (combined) return combined;
    const email = personField(row, 'email');
    if (email) return email;
    return 'Anonymous';
  };

  const columns: ColumnDef<AdminVisitorRow>[] = [
    {
      accessorKey: 'visitor_id',
      header: 'Visitor ID',
      cell: ({ row }) => {
        const id = row.original.visitor_id;
        return id ? <span className="font-mono text-xs">{id.substring(0, 8)}…</span> : 'N/A';
      },
    },
    {
      id: 'name',
      header: 'Name / Email',
      cell: ({ row }) => <span>{getDisplayName(row.original)}</span>,
    },
    {
      accessorKey: 'organization_name',
      header: 'Organization',
      cell: ({ row }) => row.original.organization_name || 'N/A',
    },
    {
      accessorKey: 'conversation_count',
      header: 'Conversations',
      cell: ({ row }) => row.original.conversation_count ?? 0,
    },
    {
      accessorKey: 'first_seen_at',
      header: 'First Seen',
      cell: ({ row }) =>
        row.original.first_seen_at ? formatDate(row.original.first_seen_at) : 'N/A',
    },
    {
      accessorKey: 'last_seen_at',
      header: 'Last Seen',
      cell: ({ row }) =>
        row.original.last_seen_at ? formatDate(row.original.last_seen_at) : 'N/A',
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          disabled={!row.original.visitor_id}
          onClick={() =>
            row.original.visitor_id && navigate(`/admin/visitors/${row.original.visitor_id}`)
          }
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  const table = useReactTable({
    data: visitors,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading && visitors.length === 0) {
    return (
      <TabsContent value="visitors" className="space-y-4">
        <LoadingState label="Loading visitors..." className="min-h-80" />
      </TabsContent>
    );
  }

  return (
    <TabsContent value="visitors" className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <h2 className="text-xl font-semibold">
          All Visitors ({totalVisitors})
        </h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
          <div className="flex items-center gap-2">
            <Input
              value={visitorIdQuery}
              onChange={(e) => setVisitorIdQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleFindVisitorById();
                if (e.key === 'Escape') setVisitorIdQuery('');
              }}
              placeholder="Find by visitor ID…"
              className="w-full sm:w-[300px]"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleFindVisitorById}
              disabled={!visitorIdQuery.trim()}
              title="Open visitor by ID"
            >
              <Search className="h-4 w-4 mr-1" />
              Find
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setSearch(searchInput);
                if (e.key === 'Escape') {
                  setSearchInput('');
                  setSearch('');
                }
              }}
              placeholder="Search name, email, company…"
              className="w-full sm:w-[260px]"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSearch(searchInput)}
            >
              Search
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Organization:</span>
            <OrganizationCombobox
              organizations={organizations}
              value={selectedOrg}
              onChange={setSelectedOrg}
            />
          </div>
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No visitors found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Page {page} of {totalPages}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
          >
            Next
          </Button>
        </div>
      </div>
    </TabsContent>
  );
};

export default VisitorsTab;
